import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import levelsProgressStarIcon from "../../assets/svg/levels-progress-star.svg";
import levelsProgressStarBlurIcon from "../../assets/svg/levels-progress-star-blur.svg";

import { constraint } from "../../helpers";
import ShowIf from "../show-if/ShowIf";
import CandidateCourseModel from "../../api/models/CandidateCourseModel";

type LevelProgressHomeStudentProps = {
  listOfLevels: CandidateCourseModel[];
};
interface ProgressBarProps {
  levelDetails: CandidateCourseModel;
}

export default function LevelProgressHomeStudent(
  props: LevelProgressHomeStudentProps
) {
  const { listOfLevels } = props;
  const [activeLevel, setActiveLevel] = useState<number>(0);
  const LocalBasePath = "page.student.dashboard.room";
  const { t } = useTranslation("", {
    keyPrefix: LocalBasePath,
  });
  const renderLevels: JSX.Element[] = useMemo(() => {
    if (listOfLevels?.length) {
      const levelsElement = listOfLevels.map(
        (level: CandidateCourseModel, index: number) => {
          let { totalSteps, stepsCompleted = 0, title } = level;
          stepsCompleted = constraint(stepsCompleted, 0, totalSteps);
          const isLevelInProgress =
            stepsCompleted > 0 && stepsCompleted < totalSteps;
          const isLevelCompleted = stepsCompleted === totalSteps;
          if (
            (isLevelCompleted || isLevelInProgress) &&
            activeLevel !== index
          ) {
            return (
              <div
                className="flex flex-col flex-shrink-0 items-center"
                onClick={() => setActiveLevel(index)}
              >
                <img
                  src={levelsProgressStarIcon}
                  className="w-6 h-6 sm:h-9 sm:w-9  "
                  alt="levels Progress Star Icon"
                />
                <h3 className="text-xs sm:text-sm  sm:leading-6 font-medium text-gray-800">
                  {t("levelNumber", { num: index + 1 })}
                </h3>
              </div>
            );
          } else if (activeLevel === index) {
            return (
              <div className="flex flex-col flex-shrink-0 items-center">
                <img
                  src={levelsProgressStarIcon}
                  className="h-6 w-6 sm:w-9 sm:h-9  opacity-70"
                  alt="levels active Star Icon"
                />
                <h3 className="text-xs  sm:text-sm  sm:leading-6 font-medium text-gray-800">
                  {t("levelNumber", { num: index + 1 })}
                </h3>
              </div>
            );
          }
          return (
            <div
              className="flex flex-col flex-shrink-0 items-center"
              onClick={() => setActiveLevel(index)}
            >
              <img
                src={levelsProgressStarBlurIcon}
                className="w-6 h-6  sm:w-9 sm:h-9  "
                alt="levels active Star Icon"
              />
              <h3 className="text-xs sm:text-sm  sm:leading-6 font-medium text-gray-300">
                {t("levelNumber", { num: index + 1 })}
              </h3>
            </div>
          );
        }
      );
      return levelsElement;
    }
    return [];
  }, [listOfLevels, activeLevel]);

  return (
    <div className="flex flex-col gap-2.5 items-start  ">
      <div className="flex flex-row  cursor-pointer justify-around w-full">
        {renderLevels}
      </div>
      <div className="flex flex-col gap-3 w-full">
        <ShowIf condition={!!listOfLevels[activeLevel]}>
          <LevelHeader levelDetails={listOfLevels[activeLevel]} />
        </ShowIf>
        <ShowIf condition={!!listOfLevels[activeLevel]}>
          <ProgressBar levelDetails={listOfLevels[activeLevel]} />
        </ShowIf>
      </div>
    </div>
  );
}

const Lesson = ({ passed = false }) => {
  return (
    <div
      className={`w-full h-3 rounded ${
        passed ? "bg-green-500" : "bg-gray-200"
      }`}
    ></div>
  );
};

const ProgressBar = ({ levelDetails }: ProgressBarProps) => {
  const { totalSteps, stepsCompleted = 0 } = levelDetails;
  const lessons = [...Array(totalSteps)].map((_, index) => {
    return <Lesson passed={index < stepsCompleted} />;
  });
  return (
    <div className="flex flex-row gap-1">{React.Children.toArray(lessons)}</div>
  );
};

const LevelHeader = ({ levelDetails }: ProgressBarProps) => {
  const { totalSteps, stepsCompleted = 0 } = levelDetails;
  const { t, i18n } = useTranslation("", {
    keyPrefix: "page.candidate.dashboard.levelProgress",
  });

  return (
    <div className="flex flex-row gap-3 justify-end">
      <div className="flex flex-row font-medium">
        <div className=" text-gray-400 flex items-end">
          <span className="text-base leading-[21px]">{totalSteps}</span>
        </div>
        <div className="text-xl leading-6">{`${
          i18n.dir() === "rtl" ? "/" : "\\"
        }${stepsCompleted}`}</div>
      </div>
    </div>
  );
};
